// Exclusion-RA draft preview — one system, one class profile, review-first.
//
// Left: the selected profile, the project's screening rows with the gate
// verdict under THAT profile (why a row is or is not draftable), existing
// drafts. Right: the prepared draft — per claim the precedent value, the
// proposed value, the model's reasoning and its verified quotes (a wiring
// page opens inline), and accept / reject / edit with the same provenance
// the model is held to; below it the deterministic grades with the rule
// behind each value and an overall state that is green only when every
// input is a reviewed value. Preparing a draft is a paid model call and
// asks for a spend cap first. Nothing here finalizes anything.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Loader2, RefreshCw, ShieldAlert, Trash2, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { API_BASE_URL, fetchWithAuthRetry } from '@/lib/apiClient'
import {
  raDraftsApi, type Citation, type DraftCandidate, type DraftCandidatesResponse, type DraftClaim, type DraftEvidence,
  type DraftListItem, type DraftPayload, type GradeStep, type OverallState, type ReviewBody,
} from '@/lib/raDraftsApi'
import {
  draftDroppedFields,
  draftErrorMessage,
  draftFailedReason,
  verifiedVesselCitationIds,
} from '@/lib/raDraftPreview'

interface Props {
  projectId: string
  initialProfileCode: string | null
  canEdit: boolean
  reviewOnly?: boolean
  draftIds?: string[]
}

const ROUTING_LABEL: Record<string, string> = {
  exclusion_candidate: 'Exclusion candidate',
  target: 'Target (CSDD/TP)',
  further_review: 'Further review',
  out_of_scope: 'Out of scope',
  unreviewed: 'Not human-reviewed',
  blocked: 'Blocked',
}

const CHANGE_STYLE: Record<string, string> = {
  unchanged: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  added: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  replaced: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  removed_by_explicit_evidence: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  conflicting: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  unknown: 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
}

const STATE_STYLE: Record<OverallState | 'missing' | 'unconfirmed', string> = {
  confirmed: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  provisional: 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  unconfirmed: 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  unresolved: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  missing: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
}

const DEFAULT_MAX_COST_USD = 1.0

function fmt(v: unknown): string {
  if (v == null) return '—'
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  if (Array.isArray(v)) {
    if (v.every(x => typeof x === 'string')) return (v as string[]).join('; ') || '(empty list)'
    return v.map(x => {
      const o = x as Record<string, unknown> | null
      if (o && typeof o === 'object' && 'counterpart' in o) return `${o.counterpart} — ${o.medium}${o.protocol ? ` (${o.protocol})` : ''}`
      return JSON.stringify(x)
    }).join('\n') || '(empty list)'
  }
  if (typeof v === 'object' && 'compliance_status' in (v as object)) {
    const o = v as { compliance_status: string | null; explanation: string | null }
    return `${o.compliance_status ?? '—'}${o.explanation ? ` — ${o.explanation}` : ''}`
  }
  return JSON.stringify(v)
}

export const RaDraftPreviewPanel: React.FC<Props> = ({
  projectId,
  initialProfileCode,
  canEdit,
  reviewOnly = false,
  draftIds,
}) => {
  const [profileCode, setProfileCode] = useState<string | null>(initialProfileCode)
  const [cands, setCands] = useState<DraftCandidatesResponse | null>(null)
  const [drafts, setDrafts] = useState<DraftListItem[]>([])
  const [payload, setPayload] = useState<DraftPayload | null>(null)
  const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null)
  const [preparingTarget, setPreparingTarget] = useState<{ screeningId: string; profileCode: string | null } | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showAllRows, setShowAllRows] = useState(false)
  const profileRef = useRef(profileCode)
  const selectedDraftRef = useRef<string | null>(null)
  const loadSequence = useRef(0)
  const payloadSequence = useRef(0)
  const prepareSequence = useRef(0)

  const selectDraft = useCallback((id: string | null) => {
    selectedDraftRef.current = id
    setSelectedDraftId(id)
  }, [])

  const load = useCallback(async (code: string | null, silent = false) => {
    const sequence = ++loadSequence.current
    if (!silent) {
      setLoading(true)
      setError(null)
    }
    try {
      const [c, d] = await Promise.all([raDraftsApi.candidates(projectId, code), raDraftsApi.list(projectId)])
      if (sequence !== loadSequence.current || code !== profileRef.current) return null
      setCands(c)
      setDrafts(d)
      if (!code && c.profile) {
        profileRef.current = c.profile.profile_code
        setProfileCode(c.profile.profile_code)
      }
      return { candidates: c, drafts: d }
    } catch (e) {
      if (sequence === loadSequence.current && !silent) setError(draftErrorMessage(e))
      return null
    } finally {
      if (sequence === loadSequence.current && !silent) setLoading(false)
    }
  }, [projectId])

  const open = useCallback(async (id: string, silent = false) => {
    const sequence = ++payloadSequence.current
    selectDraft(id)
    if (!silent) {
      setBusy(`open:${id}`)
      setError(null)
    }
    try {
      const next = await raDraftsApi.get(id)
      if (sequence === payloadSequence.current && selectedDraftRef.current === id) setPayload(next)
    } catch (e) {
      if (sequence === payloadSequence.current && !silent) setError(draftErrorMessage(e))
    } finally {
      if (sequence === payloadSequence.current && !silent) setBusy(null)
    }
  }, [selectDraft])

  useEffect(() => {
    profileRef.current = initialProfileCode
    loadSequence.current++
    payloadSequence.current++
    prepareSequence.current++
    setProfileCode(initialProfileCode)
    setCands(null)
    setDrafts([])
    setPayload(null)
    selectDraft(null)
    setPreparingTarget(null)
    setBusy(null)
    setError(null)
    setLoading(true)
  }, [projectId, initialProfileCode, selectDraft])

  useEffect(() => {
    profileRef.current = profileCode
    void load(profileCode)
  }, [load, profileCode])

  const prepare = async (row: DraftCandidate) => {
    const supersede = row.active_drafts > 0
    if (supersede && !window.confirm('An active draft exists for this system and profile. Replace it?')) return
    const sequence = ++prepareSequence.current
    const target = { screeningId: row.id, profileCode }
    setPreparingTarget(target)
    setBusy(`prepare:${row.id}`); setError(null)
    try {
      const p = await raDraftsApi.prepare(projectId, { screening_system_id: row.id, profile_code: profileCode, confirmed: true, max_cost_usd: DEFAULT_MAX_COST_USD, supersede })
      if (sequence !== prepareSequence.current || profileRef.current !== target.profileCode) return
      selectDraft(p.draft.id)
      setPayload(p)
      setPreparingTarget(null)
      await load(target.profileCode, true)
    } catch (e) {
      if (sequence !== prepareSequence.current) return
      setError(draftErrorMessage(e))
      const refreshed = await load(target.profileCode, true)
      const recoverable = refreshed?.drafts.find(d =>
        d.screening_system_id === target.screeningId &&
        d.compliance_profile_code === target.profileCode &&
        (d.status === 'failed' || d.status === 'preparing'),
      )
      if (recoverable) await open(recoverable.id, true)
      setPreparingTarget(null)
    } finally {
      if (sequence === prepareSequence.current) setBusy(null)
    }
  }
  const discard = async (id: string) => {
    if (!window.confirm('Discard this draft? Its claims and review history stay stored but it can no longer be reviewed.')) return
    setBusy(`discard:${id}`); setError(null)
    try {
      await raDraftsApi.discard(id)
      if (selectedDraftRef.current === id) { setPayload(null); selectDraft(null) }
      await load(profileRef.current, true)
    }
    catch (e) { setError(draftErrorMessage(e)) }
    finally { setBusy(null) }
  }
  const review = async (claim: DraftClaim, body: ReviewBody) => {
    if (!payload?.draft.id) return
    setBusy(`claim:${claim.id}`); setError(null)
    try {
      const next = await raDraftsApi.review(payload.draft.id, claim.id, body)
      if (selectedDraftRef.current === payload.draft.id) setPayload(next)
      await load(profileRef.current, true)
    }
    catch (e) { setError(draftErrorMessage(e)) }
    finally { setBusy(null) }
  }

  // A preparation can outlive the initiating request or browser view. Poll
  // persisted rows, attach to the placeholder as soon as it appears, and
  // keep the selected payload current until it reaches a terminal state.
  const shouldPoll = preparingTarget != null || drafts.some(d =>
    d.status === 'preparing' && d.compliance_profile_code === profileCode,
  )
  useEffect(() => {
    if (!shouldPoll) return
    let cancelled = false
    const tick = async () => {
      const result = await load(profileRef.current, true)
      if (cancelled || !result) return
      let id = selectedDraftRef.current
      if (preparingTarget) {
        const matching = result.drafts.find(d =>
          d.screening_system_id === preparingTarget.screeningId &&
          d.compliance_profile_code === preparingTarget.profileCode &&
          ['preparing', 'prepared', 'failed'].includes(d.status),
        )
        if (matching) {
          id = matching.id
          if (matching.status !== 'preparing') setPreparingTarget(null)
        }
      }
      if (!id) {
        id = result.drafts.find(d =>
          d.status === 'preparing' && d.compliance_profile_code === profileRef.current,
        )?.id ?? null
      }
      if (id) await open(id, true)
    }
    void tick()
    const timer = window.setInterval(() => { void tick() }, 2500)
    return () => { cancelled = true; window.clearInterval(timer) }
  }, [load, open, preparingTarget, shouldPoll])

  const candidateRows = useMemo(() => {
    const rows = cands?.rows ?? []
    return showAllRows ? rows : rows.filter(r => r.gate.ok || r.status === 'confirmed' || r.status === 'edited')
  }, [cands, showAllRows])
  const draftIdKey = draftIds?.join('|') ?? ''
  const visibleDrafts = useMemo(() => {
    if (!reviewOnly) return drafts
    if (!draftIdKey) return []
    const allowed = new Set(draftIdKey.split('|'))
    return drafts.filter(draft => allowed.has(draft.id))
  }, [draftIdKey, drafts, reviewOnly])

  useEffect(() => {
    if (!reviewOnly || selectedDraftRef.current || visibleDrafts.length === 0) return
    void open(visibleDrafts[0].id)
  }, [open, reviewOnly, visibleDrafts])

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {reviewOnly ? 'Generated drafts — review only' : 'Exclusion RA — draft preview (one system)'}
          </h3>
          <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
            Historical assessment = precedent only. Every vessel-specific value needs a verified quote from this vessel's evidence. Grades are computed by code from reviewed values. {reviewOnly ? 'Paid preparation is disabled in this Documents review context.' : 'Nothing is written to the RA document from this screen.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!reviewOnly && cands && cands.profiles.length > 0 && (
            <label className="flex items-center gap-1 text-[11px] text-slate-600 dark:text-slate-300">
              Class profile
              <select
                className="rounded border border-slate-300 bg-white px-1.5 py-1 text-[11px] dark:border-slate-600 dark:bg-slate-900"
                value={profileCode ?? ''}
                onChange={e => {
                  const code = e.target.value || null
                  profileRef.current = code
                  loadSequence.current++
                  payloadSequence.current++
                  prepareSequence.current++
                  setPayload(null)
                  selectDraft(null)
                  setPreparingTarget(null)
                  setBusy(null)
                  setError(null)
                  setLoading(true)
                  setProfileCode(code)
                }}
              >
                {cands.profiles.map(p => (
                  <option key={p.profile_code} value={p.profile_code}>{p.position === 1 ? 'Primary' : 'Secondary'} · {p.class_society} · {p.display_name}</option>
                ))}
              </select>
            </label>
          )}
          <Button size="sm" variant="outline" onClick={() => { void load(profileRef.current) }} disabled={loading}>
            <RefreshCw className={'mr-1 h-3.5 w-3.5 ' + (loading ? 'animate-spin' : '')} />Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0" /><span className="whitespace-pre-wrap break-words">{error}</span>
        </div>
      )}

      {!reviewOnly && cands && (
        <div className="mt-3 flex flex-wrap gap-1.5 text-[10px]">
          {cands.profile && (
            <span className="rounded bg-blue-50 px-1.5 py-0.5 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              {cands.profile.class_society} · {cands.profile.function_list_size} functions on the class list
            </span>
          )}
          {Object.entries(cands.summary).map(([k, n]) => (
            <span key={k} className={'rounded px-1.5 py-0.5 ' + (k === 'exclusion_candidate' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300')}>
              {ROUTING_LABEL[k] ?? k}: {n}
            </span>
          ))}
          <button type="button" className="ml-auto text-[10px] text-blue-600 hover:underline dark:text-blue-400" onClick={() => setShowAllRows(s => !s)}>
            {showAllRows ? 'Show reviewed rows only' : 'Show all in-scope rows'}
          </button>
        </div>
      )}

      <div className="mt-3 grid gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
        <div className="space-y-3">
          {!reviewOnly && <div className="max-h-[420px] overflow-auto rounded-lg border border-slate-200 dark:border-slate-700">
            {loading && !cands && (
              <p className="flex items-center gap-2 p-3 text-xs text-slate-500 dark:text-slate-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading draft candidates…
              </p>
            )}
            {!loading && candidateRows.length === 0 && (
              <p className="p-3 text-xs text-slate-500 dark:text-slate-400">
                No human-confirmed in-scope screening row. Confirm rows in the Yard Wiring Diagram tab first; unconfirmed output is never drafted.
              </p>
            )}
            {candidateRows.map(r => (
              <div key={r.id} className="border-b border-slate-100 px-3 py-2 text-xs last:border-b-0 dark:border-slate-700/60">
                <div className="flex items-start gap-2">
                  {r.gate.ok ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" /> : <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />}
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-800 dark:text-slate-100">{r.system_name}</div>
                    <div className="text-[10px] text-slate-500 dark:text-slate-400">
                      {r.maker ?? '—'} · {r.model ?? '—'} · CBS {r.cbs ?? '—'} · IP {r.ip ?? '—'} ({r.ip_basis ?? '—'}) · {r.status}
                    </div>
                    <div className="mt-0.5 text-[10px] font-medium text-slate-600 dark:text-slate-300">
                      {ROUTING_LABEL[r.gate.routing] ?? r.gate.routing}
                      {r.gate.class_scope && <span className="text-slate-400"> · scope under {r.gate.class_scope.class_society}: {r.gate.class_scope.status}{r.gate.class_scope.function_under_profile ? ` (${r.gate.class_scope.function_under_profile})` : ''}</span>}
                    </div>
                    {r.gate.reasons.map((x, i) => <div key={i} className="text-[10px] text-slate-500 dark:text-slate-400">• {x}</div>)}
                    {r.gate.warnings.map((x, i) => <div key={`w${i}`} className="text-[10px] text-amber-700 dark:text-amber-300">⚠ {x}</div>)}
                  </div>
                  {canEdit && r.gate.ok && (
                    <Button size="sm" onClick={() => prepare(r)} disabled={busy != null}>
                      {busy === `prepare:${r.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : r.active_drafts ? 'Re-prepare' : 'Prepare'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>}

          {reviewOnly && loading && visibleDrafts.length === 0 && (
            <p className="flex items-center gap-2 p-3 text-xs text-slate-500 dark:text-slate-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading generated drafts…
            </p>
          )}
          {!loading && reviewOnly && visibleDrafts.length === 0 && (
            <p className="rounded-lg border border-slate-200 p-3 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
              The generated draft records are not available yet. Refresh the run before finalizing.
            </p>
          )}
          {visibleDrafts.length > 0 && (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="border-b border-slate-100 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-700/60 dark:text-slate-400">Drafts</div>
              {visibleDrafts.map(d => (
                <div key={d.id} className={'flex items-center gap-2 border-b border-slate-100 px-3 py-2 text-xs last:border-b-0 dark:border-slate-700/60 ' + (selectedDraftId === d.id ? 'bg-blue-50 dark:bg-blue-900/20' : '')}>
                  <button type="button" onClick={() => open(d.id)} className="min-w-0 flex-1 text-left hover:underline">
                    <div className="font-medium text-slate-800 dark:text-slate-100">{d.candidate_name}</div>
                    <div className="text-[10px] text-slate-500 dark:text-slate-400">
                      {d.class_society} · {d.status}{d.overall_state ? ` · ${d.overall_state}` : ''} · {d.pending_count}/{d.claim_count} pending ({d.review_required_pending} need review)
                    </div>
                  </button>
                  {canEdit && !reviewOnly && ['prepared', 'in_review', 'approved', 'failed', 'preparing'].includes(d.status) && (
                    <button type="button" title="Discard draft" disabled={busy != null} onClick={() => discard(d.id)} className="text-slate-400 hover:text-red-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="min-w-0">
          {!payload && busy?.startsWith('open:') && <p className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading draft…</p>}
          {!payload && !busy?.startsWith('open:') && <p className="text-xs text-slate-500 dark:text-slate-400">{reviewOnly ? 'Open a generated draft to review it.' : 'Prepare a candidate or open a prepared draft.'}</p>}
          {payload && <DraftView payload={payload} canEdit={canEdit} busy={busy} onReview={review} />}
        </div>
      </div>
    </section>
  )
}

// ── draft view ─────────────────────────────────────────────────────────

function DraftView({ payload, canEdit, busy, onReview }: {
  payload: DraftPayload
  canEdit: boolean
  busy: string | null
  onReview: (c: DraftClaim, body: ReviewBody) => void
}) {
  const { draft, baseline, baseline_match, evidence, claims, computed, gate } = payload
  const evById = useMemo(() => new Map(evidence.map(e => [e.evidence_id, e])), [evidence])
  const [openEv, setOpenEv] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [accepting, setAccepting] = useState<string | null>(null)
  const needReview = claims.filter(c => c.requires_review && c.review_status === 'pending').length
  const overall = computed?.overall_state ?? null
  const failedReason = draftFailedReason(payload)
  const droppedFields = draftDroppedFields(payload)
  const history = draft.generation_history ?? []

  return (
    <div className="space-y-3 text-xs">
      <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-slate-800 dark:text-slate-100">{draft.candidate_name}</span>
          <span className="text-slate-500 dark:text-slate-400">{draft.candidate_maker ?? '—'} · {draft.candidate_model ?? '—'}</span>
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] dark:bg-slate-700">{draft.class_society} · {draft.compliance_profile_code}</span>
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] dark:bg-slate-700">{draft.status}</span>
          {overall && <span className={'rounded px-1.5 py-0.5 text-[10px] font-semibold ' + STATE_STYLE[overall]}>{overall}</span>}
        </div>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <div className="rounded bg-slate-50 p-2 dark:bg-slate-900/40">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Precedent (historical — never evidence)</div>
            {baseline ? (
              <div className="mt-0.5 text-slate-700 dark:text-slate-200">
                {baseline.system_name} — {baseline.supplier ?? '—'} · hull {baseline.source_hull_number ?? '?'} · {baseline.class_society ?? '?'}
                {baseline_match?.chosen && <div className="text-[10px] text-slate-500 dark:text-slate-400">{baseline_match.chosen.tier} · score {baseline_match.chosen.score} · {baseline_match.chosen.reasoning}</div>}
              </div>
            ) : <div className="mt-0.5 text-amber-700 dark:text-amber-300">No precedent matched; every field rests on current evidence or is unknown.</div>}
          </div>
          <div className="rounded bg-slate-50 p-2 dark:bg-slate-900/40">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">1st RA gate under {draft.compliance_profile_code}</div>
            <div className="mt-0.5 text-slate-700 dark:text-slate-200">
              {ROUTING_LABEL[gate.routing] ?? gate.routing}
              {gate.class_scope?.function_under_profile ? ` · function "${gate.class_scope.function_under_profile}"` : ''} · {evidence.length} evidence items · {claims.length} claims · {needReview} need review
            </div>
            {gate.warnings.map((w, i) => <div key={i} className="text-[10px] text-amber-700 dark:text-amber-300">⚠ {w}</div>)}
          </div>
        </div>
        {payload.evidence_notes?.length > 0 && (
          <div className="mt-2 text-[10px] text-slate-500 dark:text-slate-400">{payload.evidence_notes.map((n, i) => <div key={i}>• {n}</div>)}</div>
        )}
        {draft.status === 'preparing' && (
          <div className="mt-2 flex items-center gap-2 rounded border border-blue-200 bg-blue-50 p-2 text-[11px] text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Preparation is running. This preview refreshes automatically.
          </div>
        )}
        {draft.status === 'failed' && (
          <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-[11px] text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            <div className="font-semibold">Preparation failed</div>
            <div className="mt-0.5 whitespace-pre-wrap break-words">{failedReason ?? 'No failure reason was recorded.'}</div>
            <div className="mt-1 text-[10px]">Use Re-prepare on the candidate after correcting the cause, or discard this failed attempt.</div>
          </div>
        )}
        {droppedFields.length > 0 && (
          <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-[10px] text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            <span className="font-semibold">Dropped unsupported fields:</span> {droppedFields.join(', ')}
          </div>
        )}
        {history.length > 0 && (
          <details className="mt-2 text-[10px] text-slate-500 dark:text-slate-400">
            <summary className="cursor-pointer">Generation and review history ({history.length})</summary>
            <div className="mt-1 space-y-1">
              {history.map((event, index) => (
                <div key={`${event.at ?? 'event'}-${index}`} className="rounded bg-slate-50 px-2 py-1 dark:bg-slate-900/40">
                  <span className="font-medium text-slate-700 dark:text-slate-200">{event.action ?? 'event'}</span>
                  {event.at ? ` · ${new Date(event.at).toLocaleString()}` : ''}
                  {event.by ? ` · ${event.by}` : ''}
                  {event.field ? ` · ${event.field}` : ''}
                  {event.note ? ` — ${event.note}` : ''}
                  {event.error ? <div className="text-red-600 dark:text-red-300">{event.error}</div> : null}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full min-w-[960px] table-fixed border-collapse text-left">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500 dark:bg-slate-900/40">
            <tr>
              <th className="w-[14%] px-2 py-1.5">Field</th>
              <th className="w-[18%] px-2 py-1.5">Historical value</th>
              <th className="w-[20%] px-2 py-1.5">Proposed value</th>
              <th className="w-[28%] px-2 py-1.5">Reasoning · verified quotes</th>
              <th className="w-[20%] px-2 py-1.5">Review</th>
            </tr>
          </thead>
          <tbody>
            {claims.map(c => (
              <tr key={c.id} className={'border-t border-slate-100 align-top dark:border-slate-700/60 ' + (c.requires_review && c.review_status === 'pending' ? 'bg-amber-50/40 dark:bg-amber-900/10' : '')}>
                <td className="px-2 py-1.5">
                  <div className="font-medium text-slate-800 dark:text-slate-100">{c.field}</div>
                  <span className={'mt-0.5 inline-block rounded px-1 py-0.5 text-[10px] ' + (CHANGE_STYLE[c.change_type] ?? '')}>{c.change_type}</span>
                  {c.validation_notes.length > 0 && <div className="mt-0.5 text-[10px] text-slate-400">{c.validation_notes.join(', ')}</div>}
                </td>
                <td className="whitespace-pre-wrap break-words px-2 py-1.5 text-slate-600 dark:text-slate-300">{fmt(c.baseline_value)}</td>
                <td className="whitespace-pre-wrap break-words px-2 py-1.5 text-slate-800 dark:text-slate-100">
                  {c.review_status === 'edited' ? <><span className="text-[10px] text-blue-600">edited: </span>{fmt(c.reviewed_value)}</> : fmt(c.proposed_value)}
                  <div className="text-[10px] text-slate-400">confidence {Math.round((c.confidence ?? 0) * 100)}%</div>
                </td>
                <td className="px-2 py-1.5 text-slate-600 dark:text-slate-300">
                  <div className="whitespace-pre-wrap break-words">{c.reasoning || '—'}</div>
                  <div className="mt-1 space-y-0.5">
                    {c.citations.map((cit, i) => (
                      <CitationLine key={`${cit.evidence_id}-${i}`} cit={cit} ev={evById.get(cit.evidence_id)}
                        open={openEv === `${c.id}:${i}`} onToggle={() => setOpenEv(openEv === `${c.id}:${i}` ? null : `${c.id}:${i}`)} />
                    ))}
                    {c.citations.length === 0 && <span className="text-[10px] text-slate-400">no citation</span>}
                  </div>
                  {(c.reviewed_evidence_ids.length > 0 || c.review_reason || c.reviewed_by || c.reviewed_at || c.review_note) && (
                    <div className="mt-1 rounded bg-blue-50 p-1 text-[10px] text-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
                      <div>
                        Reviewed{c.reviewed_by ? ` by ${c.reviewed_by}` : ''}
                        {c.reviewed_at ? ` · ${new Date(c.reviewed_at).toLocaleString()}` : ''}
                      </div>
                      {c.reviewed_evidence_ids.length > 0 && (
                        <div>Evidence: {c.reviewed_evidence_ids.map(id => evById.get(id)?.title ?? id).join('; ')}</div>
                      )}
                      {c.review_reason && <div>Reason: {c.review_reason}</div>}
                      {c.review_note && <div>Note: {c.review_note}</div>}
                      {c.review_history && c.review_history.length > 0 && (
                        <details><summary className="cursor-pointer">Prior review actions ({c.review_history.length})</summary>
                          {c.review_history.map(event => (
                            <div key={event.id}>{event.review_status} · {new Date(event.created_at).toLocaleString()}{event.reviewer ? ` · ${event.reviewer}` : ''}</div>
                          ))}
                        </details>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-2 py-1.5">
                  <div className="flex items-center gap-1 text-[10px]">
                    {c.review_status === 'accepted' && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
                    {c.review_status === 'rejected' && <XCircle className="h-3.5 w-3.5 text-red-500" />}
                    {c.review_status === 'pending' && c.requires_review && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                    <span className="text-slate-600 dark:text-slate-300">{c.review_status}</span>
                  </div>
                  {canEdit && draft.id && editing !== c.id && accepting !== c.id && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      <button type="button" disabled={busy != null || c.change_type === 'unknown' || c.change_type === 'conflicting'}
                        onClick={() => setAccepting(c.id)}
                        className="rounded border border-emerald-300 px-1.5 py-0.5 text-[10px] text-emerald-700 disabled:opacity-40 dark:border-emerald-700 dark:text-emerald-300">Accept</button>
                      <button type="button" disabled={busy != null} onClick={() => onReview(c, { review_status: 'rejected' })}
                        className="rounded border border-red-300 px-1.5 py-0.5 text-[10px] text-red-700 disabled:opacity-40 dark:border-red-700 dark:text-red-300">Reject</button>
                      <button type="button" disabled={busy != null} onClick={() => setEditing(c.id)}
                        className="rounded border border-blue-300 px-1.5 py-0.5 text-[10px] text-blue-700 disabled:opacity-40 dark:border-blue-700 dark:text-blue-300">Edit</button>
                      {c.review_status !== 'pending' && (
                        <button type="button" disabled={busy != null} onClick={() => onReview(c, { review_status: 'pending' })}
                          className="rounded border border-slate-300 px-1.5 py-0.5 text-[10px] text-slate-600 disabled:opacity-40 dark:border-slate-600 dark:text-slate-300">Undo</button>
                      )}
                    </div>
                  )}
                  {canEdit && draft.id && accepting === c.id && (
                    <AcceptForm
                      claim={c}
                      evidence={evidence}
                      busy={busy != null}
                      onCancel={() => setAccepting(null)}
                      onSubmit={body => { setAccepting(null); onReview(c, body) }}
                    />
                  )}
                  {canEdit && draft.id && editing === c.id && (
                    <EditForm claim={c} evidence={evidence} busy={busy != null}
                      onCancel={() => setEditing(null)}
                      onSubmit={body => { setEditing(null); onReview(c, body) }} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {computed && <ComputedView computed={computed} />}
    </div>
  )
}

// ── citations ──────────────────────────────────────────────────────────

function CitationLine({ cit, ev, open, onToggle }: { cit: Citation; ev: DraftEvidence | undefined; open: boolean; onToggle: () => void }) {
  return (
    <div>
      <button type="button" onClick={onToggle}
        className={'inline-flex max-w-full items-center gap-1 rounded px-1 py-0.5 text-left text-[10px] ' + (cit.verified ? (cit.scope === 'vessel' ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300') : 'bg-red-50 text-red-700 line-through dark:bg-red-900/30 dark:text-red-300')}
        title={cit.verified ? (cit.scope === 'vessel' ? 'verified quote, current-vessel evidence' : 'verified quote, context only') : 'quote not found in the evidence — discarded'}>
        <span className="font-mono">{cit.evidence_id}{cit.page ? ` p${cit.page}` : ''}</span>
        {cit.quote && <span className="truncate">“{cit.quote}”</span>}
      </button>
      {open && ev && (
        <div className="mt-1 rounded border border-slate-200 bg-white p-2 text-[10px] dark:border-slate-600 dark:bg-slate-900/60">
          <div className="font-semibold">{ev.title} <span className="font-normal text-slate-400">· {ev.scope}</span></div>
          <div className="whitespace-pre-wrap break-words text-slate-600 dark:text-slate-300">{ev.excerpt ?? '—'}</div>
          {typeof ev.payload.page_text === 'string' && (
            <details className="mt-1"><summary className="cursor-pointer text-slate-500">page text</summary>
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap text-[10px]">{ev.payload.page_text as string}</pre></details>
          )}
          {ev.kind === 'wiring_page' && ev.source_id && ev.page && <PageImage drawingId={ev.source_id} page={ev.page} />}
        </div>
      )}
    </div>
  )
}

function PageImage({ drawingId, page }: { drawingId: string; page: number }) {
  const [url, setUrl] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    let objectUrl: string | null = null
    let cancelled = false
    ;(async () => {
      try {
        const r = await fetchWithAuthRetry(`${API_BASE_URL}/api/ra-screening/drawings/${drawingId}/page/${page}`, { method: 'GET' })
        if (!r.ok) { if (!cancelled) setErr(`could not render page ${page}`); return }
        const blob = await r.blob()
        objectUrl = URL.createObjectURL(blob)
        if (cancelled) { URL.revokeObjectURL(objectUrl); return }
        setUrl(objectUrl)
      } catch (e) { if (!cancelled) setErr(e instanceof Error ? e.message : String(e)) }
    })()
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [drawingId, page])
  if (err) return <div className="mt-1 text-red-600">{err}</div>
  if (!url) return <div className="mt-1 text-slate-400">rendering page {page}…</div>
  return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={`drawing page ${page}`} className="mt-1 max-h-80 w-auto rounded border border-slate-200 dark:border-slate-600" /></a>
}

// ── edit form ──────────────────────────────────────────────────────────

function AcceptForm({ claim, evidence, busy, onCancel, onSubmit }: {
  claim: DraftClaim; evidence: DraftEvidence[]; busy: boolean
  onCancel: () => void; onSubmit: (body: ReviewBody) => void
}) {
  const eligibleIds = useMemo(() => verifiedVesselCitationIds(claim), [claim])
  const evidenceById = useMemo(() => new Map(evidence.map(item => [item.evidence_id, item])), [evidence])
  const priorEligible = claim.reviewed_evidence_ids.filter(id => eligibleIds.includes(id))
  const [ids, setIds] = useState<string[]>(priorEligible.length ? priorEligible : eligibleIds)
  const [reason, setReason] = useState(claim.review_reason ?? '')
  const [note, setNote] = useState(claim.review_note ?? '')
  const needsDetermination = claim.requires_review && ids.length === 0 && !reason.trim()
  const toggle = (id: string) => setIds(current =>
    current.includes(id) ? current.filter(existing => existing !== id) : [...current, id],
  )

  return (
    <div className="mt-1 space-y-1 rounded border border-emerald-200 bg-emerald-50/40 p-2 text-[10px] dark:border-emerald-800 dark:bg-emerald-900/10">
      <div className="font-medium text-emerald-800 dark:text-emerald-200">Accept proposed value with provenance</div>
      {eligibleIds.length > 0 ? (
        <div className="max-h-28 overflow-auto rounded border border-slate-200 bg-white p-1 dark:border-slate-600 dark:bg-slate-900">
          <div className="mb-0.5 text-slate-500">Verified current-vessel citations</div>
          {eligibleIds.map(id => (
            <label key={id} className="flex items-start gap-1">
              <input type="checkbox" checked={ids.includes(id)} onChange={() => toggle(id)} />
              <span className="text-slate-700 dark:text-slate-200">{evidenceById.get(id)?.title ?? id}</span>
            </label>
          ))}
        </div>
      ) : (
        <div className="text-amber-700 dark:text-amber-300">No verified current-vessel citation is attached to this claim.</div>
      )}
      <input
        className="w-full rounded border border-slate-300 bg-white p-1 text-[10px] dark:border-slate-600 dark:bg-slate-900"
        placeholder={claim.requires_review ? 'Assessor reason (required when no citation is selected)' : 'Assessor reason (optional)'}
        value={reason}
        onChange={event => setReason(event.target.value)}
      />
      <input
        className="w-full rounded border border-slate-300 bg-white p-1 text-[10px] dark:border-slate-600 dark:bg-slate-900"
        placeholder="Review note (optional)"
        value={note}
        onChange={event => setNote(event.target.value)}
      />
      <div className="flex gap-1">
        <button
          type="button"
          disabled={busy || needsDetermination}
          onClick={() => onSubmit({
            review_status: 'accepted',
            reviewed_evidence_ids: ids,
            review_reason: reason.trim() || null,
            note: note.trim() || null,
          })}
          className="rounded border border-emerald-400 px-1.5 py-0.5 text-emerald-700 disabled:opacity-40 dark:text-emerald-300"
        >
          Confirm acceptance
        </button>
        <button type="button" onClick={onCancel} className="rounded border border-slate-300 px-1.5 py-0.5 text-slate-600 dark:border-slate-600 dark:text-slate-300">Cancel</button>
      </div>
    </div>
  )
}

function EditForm({ claim, evidence, busy, onCancel, onSubmit }: {
  claim: DraftClaim; evidence: DraftEvidence[]; busy: boolean
  onCancel: () => void; onSubmit: (body: ReviewBody) => void
}) {
  const humanOnly = claim.validation_notes.includes('human_only')
  const initial = claim.reviewed_value ?? claim.proposed_value ?? claim.baseline_value
  const [raw, setRaw] = useState(typeof initial === 'string' ? initial : initial == null ? '' : JSON.stringify(initial, null, 1))
  const [ids, setIds] = useState<string[]>(claim.reviewed_evidence_ids.length ? claim.reviewed_evidence_ids : claim.evidence_ids)
  const [reason, setReason] = useState(claim.review_reason ?? '')
  const toggle = (id: string) => setIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])
  return (
    <div className="mt-1 space-y-1 rounded border border-blue-200 bg-blue-50/40 p-2 text-[10px] dark:border-blue-800 dark:bg-blue-900/10">
      <textarea className="w-full rounded border border-slate-300 bg-white p-1 font-mono text-[10px] dark:border-slate-600 dark:bg-slate-900" rows={4} value={raw} onChange={e => setRaw(e.target.value)} />
      <div className="text-slate-500">JSON for lists / objects, plain text otherwise.</div>
      <div className="max-h-28 overflow-auto rounded border border-slate-200 bg-white p-1 dark:border-slate-600 dark:bg-slate-900">
        <div className="mb-0.5 text-slate-500">{humanOnly ? 'Evidence (optional for an assessor determination)' : 'Evidence this value rests on (at least one current-vessel item)'}</div>
        {evidence.map(e => (
          <label key={e.evidence_id} className="flex items-start gap-1">
            <input type="checkbox" checked={ids.includes(e.evidence_id)} onChange={() => toggle(e.evidence_id)} />
            <span className={e.scope === 'vessel' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'}>{e.evidence_id} <span className="text-slate-400">({e.scope})</span></span>
          </label>
        ))}
      </div>
      <input className="w-full rounded border border-slate-300 bg-white p-1 text-[10px] dark:border-slate-600 dark:bg-slate-900" placeholder={humanOnly ? 'Reason (required unless evidence is named)' : 'Note (optional)'} value={reason} onChange={e => setReason(e.target.value)} />
      <div className="flex gap-1">
        <button type="button" disabled={busy} onClick={() => {
          let v: unknown = raw
          const t = raw.trim()
          if (t.startsWith('[') || t.startsWith('{') || /^\d+$/.test(t)) { try { v = JSON.parse(t) } catch { /* keep text */ } }
          onSubmit({ review_status: 'edited', reviewed_value: v, reviewed_evidence_ids: ids, review_reason: reason.trim() || null })
        }} className="rounded border border-blue-400 px-1.5 py-0.5 text-blue-700 disabled:opacity-40 dark:text-blue-300">Save edit</button>
        <button type="button" onClick={onCancel} className="rounded border border-slate-300 px-1.5 py-0.5 text-slate-600 dark:border-slate-600 dark:text-slate-300">Cancel</button>
      </div>
    </div>
  )
}

// ── computed ───────────────────────────────────────────────────────────

function Step({ label, s }: { label: string; s: GradeStep }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded border border-slate-200 p-2 dark:border-slate-700">
      <button type="button" className="flex w-full items-center gap-1 text-left" onClick={() => setOpen(o => !o)}>
        {open ? <ChevronDown className="h-3 w-3 text-slate-400" /> : <ChevronRight className="h-3 w-3 text-slate-400" />}
        <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
        <span className="ml-auto font-semibold text-slate-800 dark:text-slate-100">{s.value ?? '—'}</span>
        <span className={'rounded px-1 text-[9px] ' + STATE_STYLE[s.state]}>{s.state}</span>
        {s.disagrees_with_baseline && <span className="rounded bg-orange-50 px-1 text-[9px] text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">≠ precedent {s.baseline}</span>}
      </button>
      {open && (
        <div className="mt-1 text-[10px] text-slate-600 dark:text-slate-300">
          <div>{s.rule}</div>
          <div className="font-mono text-slate-500">{JSON.stringify(s.inputs)}</div>
          {s.reasons.map((r, i) => <div key={i} className="text-amber-700 dark:text-amber-300">• {r}</div>)}
        </div>
      )}
    </div>
  )
}

function ResultBadge({ result, provisional, overall }: { result: string; provisional: string; overall: OverallState }) {
  const positive = result === 'exclusion_supported' || result === 'negligible_risk'
  const negative = result === 'not_eligible' || result === 'target'
  const cls = positive ? STATE_STYLE.confirmed : negative ? STATE_STYLE.unresolved : STATE_STYLE.provisional
  return (
    <span className={'ml-auto rounded px-1.5 py-0.5 text-[10px] font-semibold ' + cls} title={overall === 'confirmed' ? 'every input is a reviewed value' : `overall state ${overall}: current inputs would give "${provisional}"`}>
      {result}{result === 'provisional' ? ` (would be: ${provisional})` : ''}
    </span>
  )
}

function ComputedView({ computed }: { computed: NonNullable<DraftPayload['computed']> }) {
  if (computed.family === 'dnv') {
    return (
      <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Deterministic result (DNV criteria) · {computed.version}</div>
          <ResultBadge result={computed.result} provisional={computed.provisional_result} overall={computed.overall_state} />
        </div>
        <div className="mt-1 grid gap-1 sm:grid-cols-2">
          {[...computed.essential, ...computed.additional].map(c => (
            <div key={c.field} className="flex items-center gap-2 text-[11px]"><span className="text-slate-500">{c.field}</span><span className="ml-auto font-semibold">{c.status ?? '—'}</span><span className={'rounded px-1 text-[9px] ' + STATE_STYLE[c.state]}>{c.state}{c.derived_from_connectivity ? ' · derived' : ''}</span></div>
          ))}
        </div>
        {computed.reasons.map((r, i) => <div key={i} className="mt-1 text-[10px] text-amber-700 dark:text-amber-300">• {r}</div>)}
      </div>
    )
  }
  return (
    <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Deterministic grades · {computed.version}</div>
        <span className={'rounded px-1.5 py-0.5 text-[10px] ' + STATE_STYLE[computed.overall_state]}>{computed.overall_state}</span>
        <ResultBadge result={computed.acceptance.result} provisional={computed.acceptance.provisional_result} overall={computed.overall_state} />
      </div>
      <div className="mt-2 grid gap-1.5 sm:grid-cols-3">
        <Step label="Category" s={computed.category} />
        <Step label="Complexity CX" s={computed.cx} />
        <Step label="Connectivity CY" s={computed.cy} />
        <Step label="Attack surface AS" s={computed.as} />
        <Step label="Human factor H" s={computed.h} />
        <Step label="Likelihood L" s={computed.likelihood} />
        <Step label="Impact P" s={computed.impact} />
        <Step label="Risk level RL" s={computed.risk_level} />
        <Step label="Treatment" s={computed.treatment} />
      </div>
      <div className="mt-2 grid gap-1 sm:grid-cols-2">
        {computed.acceptance.per_criterion.map(c => (
          <div key={c.field} className="flex items-center gap-2 text-[11px]">
            <span className="text-slate-500">{c.field}</span>
            <span className="ml-auto font-semibold">{c.status ?? '—'}</span>
            <span className={'rounded px-1 text-[9px] ' + STATE_STYLE[c.state]}>{c.state}{c.derived_from_connectivity ? ' · derived from connections' : ''}</span>
          </div>
        ))}
      </div>
      {computed.acceptance.reasons.map((r, i) => <div key={i} className="mt-1 text-[10px] text-amber-700 dark:text-amber-300">• {r}</div>)}
    </div>
  )
}
